'use client';

import { useEffect, useState } from 'react';
import { Printer, Loader2, QrCode } from 'lucide-react';
import QRCode from 'qrcode';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { colors } from '@/styles/theme';
import { useTranslation } from '@/hooks/useTranslation';
import { interpolate } from './i18n';
import type { RobotUnit } from './api';

interface QrPrintDialogProps {
  /** 受控开关 */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 要打印的机器人列表（已选） */
  robots: Array<Pick<RobotUnit, 'id' | 'ffsn'> & { model?: { name?: string; code?: string } | null; sku?: { code?: string } | null }>;
  /** 二维码扫描后跳转的 URL 前缀（默认当前 origin/robot-manager/by-ffsn/{ffsn}） */
  urlPrefix?: string;
}

/**
 * 批量打印机器人二维码 Dialog
 *
 * - 每台机器人生成一个 QR（含 FF SN + deep link URL）
 * - A4 4×6=24 张/页布局，标签 65mm × 45mm
 * - 浏览器原生 window.print() 触发打印，print CSS 隐藏非标签内容
 * - 业务方场景：贴在机器人本体 / 包装箱上，扫码进详情页
 */
export function QrPrintDialog({ open, onOpenChange, robots, urlPrefix }: QrPrintDialogProps) {
  const { t } = useTranslation();
  const rm = t.robotManager;
  const [qrUrls, setQrUrls] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open || robots.length === 0) return;
    let cancelled = false;
    setGenerating(true);
    const baseUrl = urlPrefix ?? (typeof window !== 'undefined' ? `${window.location.origin}/robot-manager/by-ffsn` : '/robot-manager/by-ffsn');

    Promise.all(
      robots.map(async (r) => {
        try {
          // 扫码内容：deep link 到机器人详情页（通过 ffsn 查找）
          const data = `${baseUrl}/${encodeURIComponent(r.ffsn)}`;
          const url = await QRCode.toDataURL(data, {
            width: 240,
            margin: 1,
            color: { dark: '#000000', light: '#ffffff' },
            errorCorrectionLevel: 'M',
          });
          return [r.id, url] as const;
        } catch (e) {
          console.error('QR gen failed for', r.ffsn, e);
          return [r.id, ''] as const;
        }
      }),
    ).then((pairs) => {
      if (cancelled) return;
      const map: Record<string, string> = {};
      for (const [id, url] of pairs) map[id] = url;
      setQrUrls(map);
      setGenerating(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open, robots, urlPrefix]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col print-dialog">
        <DialogHeader className="no-print">
          <DialogTitle>
            <QrCode className="w-5 h-5 inline-block mr-2 -mt-0.5" />
            {interpolate(rm.qr.batchPrintTitle, { count: robots.length })}
          </DialogTitle>
          <DialogDescription>
            {rm.qr.batchPrintDesc}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto qr-print-area">
          {generating ? (
            <div className="flex items-center justify-center py-12" style={{ color: colors.textSecondary }}>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {rm.qr.generating}
            </div>
          ) : robots.length === 0 ? (
            <div className="text-center py-12" style={{ color: colors.textTertiary }}>
              {rm.qr.selectFirst}
            </div>
          ) : (
            <div className="qr-grid grid grid-cols-4 gap-0">
              {robots.map((r) => (
                <div
                  key={r.id}
                  className="qr-cell flex flex-col items-center justify-between p-2"
                  style={{
                    border: '1px dashed #999',
                    width: '65mm',
                    height: '45mm',
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                  }}
                >
                  {qrUrls[r.id] ? (
                    <img
                      src={qrUrls[r.id]}
                      alt={r.ffsn}
                      className="qr-img"
                      style={{ width: '28mm', height: '28mm' }}
                    />
                  ) : (
                    <div style={{ width: '28mm', height: '28mm', backgroundColor: '#eee' }} />
                  )}
                  <div className="qr-info text-center" style={{ width: '100%' }}>
                    <div style={{ fontSize: '9pt', fontWeight: 600, fontFamily: 'monospace' }}>
                      {r.ffsn}
                    </div>
                    {r.model?.name && (
                      <div style={{ fontSize: '7pt', color: '#666', lineHeight: 1.2 }}>
                        {r.model.name}
                        {r.sku?.code ? ` · ${r.sku.code}` : ''}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="no-print">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {rm.qr.closeDialog}
          </Button>
          <Button onClick={handlePrint} disabled={generating || robots.length === 0}>
            <Printer className="w-4 h-4 mr-1" />
            {interpolate(rm.qr.printCount, { count: robots.length })}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* 打印样式：隐藏页面其他内容，只打印 QR 网格 */}
      <style jsx global>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 8mm;
          }
          body * {
            visibility: hidden;
          }
          .qr-print-area,
          .qr-print-area * {
            visibility: visible;
          }
          .qr-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          .qr-grid {
            display: grid;
            grid-template-columns: repeat(4, 65mm);
            gap: 0;
          }
        }
      `}</style>
    </Dialog>
  );
}
